import type { Term } from '../systems/EventBus';
import { TEACH_PANELS } from './teach-panels';

export type FloorStationId =
  | 'invoice-desk'
  | 'aging-board'
  | 'collections-desk'
  | 'payments-desk'
  | 'resolution-desk';

export type WorkItemKind =
  | 'contract'
  | 'open-invoice'
  | 'aged-invoice'
  | 'payment'
  | 'short-pay'
  | 'dispute'
  | 'call-note'
  | 'close-card';

export type FloorTermId =
  | Term
  | 'accounts-receivable'
  | 'invoice'
  | 'net-30'
  | 'remittance-advice';

export interface FloorTermCopy {
  id: FloorTermId;
  title: string;
  definition: string;
}

export interface FloorChoice {
  id: string;
  label: string;
  detail: string;
  correct: boolean;
}

export interface FloorWorkItem {
  id: string;
  kind: WorkItemKind;
  title: string;
  customer: string;
  amountLabel: string;
  detail: string;
}

export interface FloorLesson {
  id: string;
  title: string;
  objective: string;
  mentorLine: string;
  stationId: FloorStationId;
  workItem: FloorWorkItem;
  challengeTitle: string;
  challengePrompt: string;
  choices: readonly FloorChoice[];
  successLine: string;
  termUnlocks: readonly FloorTermId[];
}

export const FLOOR_TERM_COPY: Record<FloorTermId, FloorTermCopy> = {
  'accounts-receivable': {
    id: 'accounts-receivable',
    title: 'accounts receivable',
    definition:
      'Money customers owe after a company sends an invoice. AR work turns that promise into cash.',
  },
  invoice: {
    id: 'invoice',
    title: 'invoice',
    definition:
      'A bill sent to a customer: what they bought, how much they owe, and when payment is due.',
  },
  'net-30': {
    id: 'net-30',
    title: 'Net 30',
    definition:
      'Payment terms meaning the customer is expected to pay within 30 days of the invoice date.',
  },
  'remittance-advice': {
    id: 'remittance-advice',
    title: 'remittance advice',
    definition:
      'Payment context from the customer that says which invoices a wire or check is meant to cover.',
  },
  'aging-bucket': {
    id: 'aging-bucket',
    title: TEACH_PANELS['aging-bucket'].title,
    definition: TEACH_PANELS['aging-bucket'].definition,
  },
  dunning: {
    id: 'dunning',
    title: TEACH_PANELS.dunning.title,
    definition: TEACH_PANELS.dunning.definition,
  },
  'cash-application': {
    id: 'cash-application',
    title: TEACH_PANELS['cash-application'].title,
    definition: TEACH_PANELS['cash-application'].definition,
  },
  'short-pay': {
    id: 'short-pay',
    title: TEACH_PANELS['short-pay'].title,
    definition: TEACH_PANELS['short-pay'].definition,
  },
  dispute: {
    id: 'dispute',
    title: TEACH_PANELS.dispute.title,
    definition: TEACH_PANELS.dispute.definition,
  },
  'promise-to-pay': {
    id: 'promise-to-pay',
    title: TEACH_PANELS['promise-to-pay'].title,
    definition: TEACH_PANELS['promise-to-pay'].definition,
  },
  'write-off': {
    id: 'write-off',
    title: TEACH_PANELS['write-off'].title,
    definition: TEACH_PANELS['write-off'].definition,
  },
  dso: {
    id: 'dso',
    title: TEACH_PANELS.dso.title,
    definition: TEACH_PANELS.dso.definition,
  },
};

export const AR_FLOOR_LESSONS: readonly FloorLesson[] = [
  {
    id: 'invoice-to-ar',
    title: 'Invoice to receivable',
    objective: 'Pick up the Acme contract and create the first invoice.',
    mentorLine:
      'AR starts when a customer owes us money. Carry the contract to the Invoice Desk and turn it into a clean invoice.',
    stationId: 'invoice-desk',
    workItem: {
      id: 'contract-acme-001',
      kind: 'contract',
      title: 'Contract',
      customer: 'Acme Corp',
      amountLabel: '$12,000',
      detail: 'Net 30 service agreement',
    },
    challengeTitle: 'Create the invoice',
    challengePrompt:
      'Acme signed for $12,000 on Net 30 terms. What does AR need to create?',
    choices: [
      {
        id: 'invoice',
        label: 'An invoice for $12,000 due in 30 days',
        detail: 'This creates the receivable AR will track until cash arrives.',
        correct: true,
      },
      {
        id: 'receipt',
        label: 'A receipt saying Acme already paid',
        detail: 'Receipts come after cash. Acme only owes us right now.',
        correct: false,
      },
      {
        id: 'write-off',
        label: 'A bad-debt write-off',
        detail: 'Too early. Nothing is overdue yet.',
        correct: false,
      },
    ],
    successLine: 'Invoice created. Acme now sits in accounts receivable until they pay.',
    termUnlocks: ['accounts-receivable', 'invoice', 'net-30'],
  },
  {
    id: 'aging-buckets',
    title: 'Sort the aging',
    objective: 'Move Acme to the correct aging bucket.',
    mentorLine:
      'When invoices sit unpaid, AR groups them by age. Older buckets get more urgent.',
    stationId: 'aging-board',
    workItem: {
      id: 'invoice-acme-90',
      kind: 'aged-invoice',
      title: 'Unpaid invoice',
      customer: 'Acme Corp',
      amountLabel: '$42,000',
      detail: '127 days past due',
    },
    challengeTitle: 'Pick the bucket',
    challengePrompt: 'Acme is 127 days past due. Which aging bucket should own this?',
    choices: [
      {
        id: 'bucket-0-30',
        label: '0-30',
        detail: 'Newer invoices live here.',
        correct: false,
      },
      {
        id: 'bucket-61-90',
        label: '61-90',
        detail: 'Serious, but not old enough for Acme.',
        correct: false,
      },
      {
        id: 'bucket-90-plus',
        label: '90+',
        detail: 'Invoices beyond 90 days are the most urgent cash risk.',
        correct: true,
      },
    ],
    successLine: 'Acme is in 90+. That is the bucket AR looks at first.',
    termUnlocks: ['aging-bucket'],
  },
  {
    id: 'dunning',
    title: 'Send the reminder',
    objective: 'Choose the right dunning action for Acme.',
    mentorLine:
      'Dunning is the reminder ladder. The trick is matching tone to how overdue the customer is.',
    stationId: 'collections-desk',
    workItem: {
      id: 'dunning-acme-001',
      kind: 'aged-invoice',
      title: '90+ follow-up',
      customer: 'Acme Corp',
      amountLabel: '$42,000',
      detail: 'Needs escalation',
    },
    challengeTitle: 'Choose dunning level',
    challengePrompt: 'Acme is in 90+. Which message fits best?',
    choices: [
      {
        id: 'friendly',
        label: 'Friendly first notice',
        detail: 'Too soft for a 90+ balance.',
        correct: false,
      },
      {
        id: 'final',
        label: 'Final notice before collections',
        detail: 'Firm, specific, and appropriate for severe aging.',
        correct: true,
      },
      {
        id: 'thanks',
        label: 'Thank-you for payment',
        detail: 'No payment has arrived.',
        correct: false,
      },
    ],
    successLine: 'Dunning sent. The account has a clear next step instead of sitting stale.',
    termUnlocks: ['dunning'],
  },
  {
    id: 'cash-application',
    title: 'Cash application',
    objective: 'Apply Peak Industries payment to the right invoices.',
    mentorLine:
      'Cash arriving is not the finish line. AR still has to match it to the right customer and invoice.',
    stationId: 'payments-desk',
    workItem: {
      id: 'payment-peak-001',
      kind: 'payment',
      title: 'Wire payment',
      customer: 'Peak Industries',
      amountLabel: '$18,000',
      detail: 'Remittance: May invoices',
    },
    challengeTitle: 'Apply the payment',
    challengePrompt:
      'Peak wired $18,000 with remittance for May invoices. What should you do?',
    choices: [
      {
        id: 'peak',
        label: 'Apply it to Peak Industries open May invoices',
        detail: 'Correct customer, correct context, clean cash application.',
        correct: true,
      },
      {
        id: 'acme',
        label: 'Apply it to Acme because Acme is oldest',
        detail: 'Oldest does not matter if the customer is wrong.',
        correct: false,
      },
      {
        id: 'unapplied',
        label: 'Leave it unapplied with no note',
        detail: 'That hides cash and leaves invoices open.',
        correct: false,
      },
    ],
    successLine: 'Payment applied. Peak now has less open AR and the cash is visible.',
    termUnlocks: ['cash-application', 'remittance-advice'],
  },
  {
    id: 'short-pay',
    title: 'Short-pay exception',
    objective: 'Handle TechStart paying less than the invoice.',
    mentorLine:
      'Not every payment matches perfectly. A short-pay is a clue, not a cleanup shortcut.',
    stationId: 'payments-desk',
    workItem: {
      id: 'shortpay-techstart-001',
      kind: 'short-pay',
      title: 'Short payment',
      customer: 'TechStart',
      amountLabel: '$4,200 / $5,000',
      detail: 'Customer says PO only approved $4,200',
    },
    challengeTitle: 'Resolve the short-pay',
    challengePrompt:
      'TechStart paid $4,200 against a $5,000 invoice. What is the best first move?',
    choices: [
      {
        id: 'investigate',
        label: 'Apply $4,200 and investigate the $800 difference',
        detail: 'The remaining balance needs a reason: dispute, credit, or collection.',
        correct: true,
      },
      {
        id: 'close',
        label: 'Close the whole invoice as paid',
        detail: 'That hides the missing $800.',
        correct: false,
      },
      {
        id: 'refund',
        label: 'Refund the payment',
        detail: 'Cash arrived. The issue is the shortage, not the whole payment.',
        correct: false,
      },
    ],
    successLine: 'Short-pay flagged. The missing $800 stays visible until resolved.',
    termUnlocks: ['short-pay'],
  },
  {
    id: 'dispute',
    title: 'Log the dispute',
    objective: 'Record Acme dispute against the right invoice.',
    mentorLine:
      'A dispute is customer pushback. Logging it clearly keeps AR from chasing the wrong thing.',
    stationId: 'resolution-desk',
    workItem: {
      id: 'dispute-acme-4412',
      kind: 'dispute',
      title: 'Dispute note',
      customer: 'Acme Corp',
      amountLabel: '$1,850',
      detail: 'Double charge on PO 4412',
    },
    challengeTitle: 'Classify the dispute',
    challengePrompt: 'Acme says PO 4412 was double-charged. How should this be logged?',
    choices: [
      {
        id: 'billing-error',
        label: 'Billing error on INV-4412',
        detail: 'Duplicate charge belongs on the specific invoice record.',
        correct: true,
      },
      {
        id: 'ignore',
        label: 'Ignore it and keep collecting',
        detail: 'Collections without dispute context damages the relationship.',
        correct: false,
      },
      {
        id: 'new-invoice',
        label: 'Create a new invoice',
        detail: 'That adds noise instead of resolving the claim.',
        correct: false,
      },
    ],
    successLine: 'Dispute logged. Now the right team can fix the invoice instead of AR guessing.',
    termUnlocks: ['dispute'],
  },
  {
    id: 'promise-to-pay',
    title: 'Secure a promise',
    objective: 'Turn a collections call into a specific commitment.',
    mentorLine:
      'A vague "soon" does not help the close. AR needs a concrete amount and date.',
    stationId: 'collections-desk',
    workItem: {
      id: 'call-atlas-001',
      kind: 'call-note',
      title: 'Customer call',
      customer: 'Atlas Trading',
      amountLabel: '$14,500',
      detail: '92 days past due',
    },
    challengeTitle: 'Record the commitment',
    challengePrompt: 'Atlas says they can pay part of the balance. Which note is useful?',
    choices: [
      {
        id: 'specific',
        label: '$7,500 by next Friday',
        detail: 'Specific amount plus specific date equals a usable promise-to-pay.',
        correct: true,
      },
      {
        id: 'soon',
        label: 'They will pay soon',
        detail: 'Too vague to forecast cash.',
        correct: false,
      },
      {
        id: 'maybe',
        label: 'They might check internally',
        detail: 'That is not a commitment.',
        correct: false,
      },
    ],
    successLine: 'Promise-to-pay recorded. The forecast now has a real amount and date.',
    termUnlocks: ['promise-to-pay'],
  },
  {
    id: 'dso-writeoff',
    title: 'Close the loop',
    objective: 'Review bad debt and the DSO board.',
    mentorLine:
      'At close, AR work rolls up to cash health. DSO shows how long invoices stay unpaid.',
    stationId: 'resolution-desk',
    workItem: {
      id: 'close-delta-001',
      kind: 'close-card',
      title: 'Close card',
      customer: 'Delta Retail',
      amountLabel: '$800',
      detail: '118 days past due, low recovery chance',
    },
    challengeTitle: 'Month-end decision',
    challengePrompt:
      'Delta has an $800 invoice at 118 days past due. Priya asks whether to write it off before reviewing DSO.',
    choices: [
      {
        id: 'approve',
        label: 'Approve write-off and mark it as bad debt',
        detail: 'Small balances can cost more to chase than they are worth.',
        correct: true,
      },
      {
        id: 'hide',
        label: 'Delete the invoice from the aging board',
        detail: 'Deleting hides history and breaks reporting.',
        correct: false,
      },
      {
        id: 'thank',
        label: 'Send a thank-you note',
        detail: 'No payment happened.',
        correct: false,
      },
    ],
    successLine:
      'Write-off recorded. DSO is still high, which means cash stayed tied up too long.',
    termUnlocks: ['write-off', 'dso'],
  },
];

export const CORE_FLOOR_TERMS: readonly FloorTermId[] = [
  'accounts-receivable',
  'invoice',
  'net-30',
  'aging-bucket',
  'dunning',
  'cash-application',
  'remittance-advice',
  'short-pay',
  'dispute',
  'promise-to-pay',
  'write-off',
  'dso',
];
